import {
  ApiClient,
  ApiError,
  type ApiKeyCreateInput,
  type ApiKeyScope,
  type ApiKeySummary,
  type Application,
} from '../lib/api.js';
import { el, formatDateTime, formatRelativeTime } from '../lib/dom.js';

export interface ApiKeysAdminDeps {
  api: ApiClient;
}

const SCOPE_LABELS: Record<ApiKeyScope, string> = {
  'deploy:start': 'Start deploys',
  'deploy:read': 'Read status & logs',
  'build:start': 'Start native builds',
  'build:read': 'Read native build status & logs',
};

const EXPIRY_CHOICES: { label: string; days: number | null }[] = [
  { label: '1 day', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: 'Never', days: null },
];

/** Opens the "API keys" modal. Returns a dismiss function. */
export function openApiKeysAdmin(deps: ApiKeysAdminDeps): () => void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const body = el('div', { class: 'app-admin-body' });
  const closeBtn = el('button', { class: 'ghost', text: 'Close' });
  const addBtn = el('button', { class: 'primary', text: '+ New key' });
  const modal = el('div', {
    class: 'modal modal-wide',
    children: [
      el('header', {
        children: [
          el('div', { class: 'modal-title', children: [el('h3', { text: 'API keys' })] }),
          el('div', { class: 'log-actions', children: [addBtn, closeBtn] }),
        ],
      }),
      body,
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);

  const dismiss = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') dismiss(); };
  document.addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (ev) => { if (ev.target === backdrop) dismiss(); });

  let apps: Application[] = [];
  const appName = (id: number) => apps.find((a) => a.id === id)?.name ?? `app #${id}`;

  const refresh = async () => {
    body.replaceChildren(el('div', { class: 'empty', text: 'Loading…' }));
    try {
      const [keysRes, appsRes] = await Promise.all([
        deps.api.listApiKeys(),
        deps.api.listApplications(),
      ]);
      apps = appsRes.applications;
      renderList(keysRes.items);
    } catch (err) {
      body.replaceChildren(
        el('div', { class: 'alert', text: err instanceof ApiError ? err.message : 'Failed to load API keys' }),
      );
    }
  };

  const renderList = (keys: ApiKeySummary[]) => {
    if (keys.length === 0) {
      body.replaceChildren(el('div', {
        class: 'empty',
        text: 'No API keys yet. Create one to let a machine (e.g. a Claude Code session) deploy and read logs with restricted rights.',
      }));
      return;
    }
    body.replaceChildren(...keys.map(renderRow));
  };

  const renderRow = (k: ApiKeySummary): HTMLElement => {
    const now = Date.now();
    const expired = k.expiresAt !== null && k.expiresAt <= now;
    const badges: HTMLElement[] = k.scopes.map((s) =>
      el('span', { class: 'pill running', text: SCOPE_LABELS[s] ?? s }),
    );
    if (k.revokedAt !== null) badges.push(el('span', { class: 'pill failed', text: 'revoked' }));
    else if (expired) badges.push(el('span', { class: 'pill timeout', text: 'expired' }));

    const restrictions = [
      k.appIds ? `apps: ${k.appIds.map(appName).join(', ')}` : 'all apps',
      k.branchPatterns ? `branches: ${k.branchPatterns.join(', ')}` : 'any branch',
      k.expiresAt !== null ? `expires ${formatDateTime(k.expiresAt)}` : 'no expiry',
    ].join(' · ');

    const usage = [
      `created ${formatDateTime(k.createdAt)} by ${k.createdBy}`,
      k.lastUsedAt !== null ? `last used ${formatRelativeTime(k.lastUsedAt)}` : 'never used',
    ].join(' · ');

    const actions: HTMLElement[] = [];
    if (k.revokedAt === null) {
      actions.push(el('button', {
        class: 'danger',
        text: 'Revoke',
        on: {
          click: async () => {
            if (!confirm(`Revoke API key "${k.name}"?\n\nAny client using it loses access immediately. This cannot be undone.`)) return;
            try {
              await deps.api.revokeApiKey(k.id);
              await refresh();
            } catch (err) {
              alert(err instanceof ApiError ? err.message : 'Revoke failed');
            }
          },
        },
      }));
    }

    return el('div', {
      class: 'volume-row',
      children: [
        el('div', {
          class: 'volume-head',
          children: [
            el('span', { class: 'volume-name', text: `${k.name} ` }),
            el('code', { text: `${k.tokenPrefix}…` }),
            el('span', { class: 'who', children: badges }),
          ],
        }),
        el('div', { class: 'volume-meta', text: restrictions }),
        el('div', { class: 'volume-meta', text: usage }),
        el('div', { class: 'volume-actions', children: actions }),
      ],
    });
  };

  // ── Create form ────────────────────────────────────────────────
  const openForm = () => {
    const field = (label: string, input: HTMLElement, hint?: string) =>
      el('label', { class: 'form-field', children: [
        el('span', { class: 'form-label', text: label }),
        input,
        hint ? el('span', { class: 'form-hint', text: hint }) : null,
      ] });

    const nameI = el('input', {
      attrs: { type: 'text', placeholder: 'claude-session-2026-07', autocapitalize: 'off', spellcheck: false },
    }) as HTMLInputElement;

    const scopeChecks = (Object.keys(SCOPE_LABELS) as ApiKeyScope[]).map((scope) => {
      const cb = el('input', { attrs: { type: 'checkbox', checked: '' } }) as HTMLInputElement;
      return {
        scope,
        cb,
        row: el('label', { class: 'inline-check', children: [cb, el('span', { text: ` ${SCOPE_LABELS[scope]} (${scope})` })] }),
      };
    });

    const allAppsCb = el('input', { attrs: { type: 'checkbox', checked: '' } }) as HTMLInputElement;
    const appChecks = apps.map((a) => {
      const cb = el('input', { attrs: { type: 'checkbox' } }) as HTMLInputElement;
      return {
        id: a.id,
        cb,
        row: el('label', { class: 'inline-check', children: [cb, el('span', { text: ` ${a.name}` })] }),
      };
    });
    const appList = el('div', { class: 'proxy-fields', children: appChecks.map((c) => c.row) });
    const syncApps = () => { appList.style.display = allAppsCb.checked ? 'none' : ''; };
    allAppsCb.addEventListener('change', syncApps);
    syncApps();

    const branchesI = el('input', {
      attrs: { type: 'text', placeholder: 'claude/*, main', autocapitalize: 'off', spellcheck: false },
    }) as HTMLInputElement;

    const expiryS = el('select') as HTMLSelectElement;
    for (const c of EXPIRY_CHOICES) {
      expiryS.append(el('option', { text: c.label, attrs: { value: c.days === null ? '' : String(c.days) } }));
    }
    expiryS.value = '7';

    const errBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
    const createBtn = el('button', { class: 'primary', text: 'Create key' });
    const cancelBtn = el('button', { class: 'ghost', text: 'Cancel' });

    const form = el('div', { class: 'app-form', children: [
      errBox,
      field('Name', nameI, 'A label describing who/what uses this key.'),
      el('div', { class: 'form-section', text: 'Permissions' }),
      ...scopeChecks.map((c) => c.row),
      el('div', { class: 'form-section', text: 'Applications' }),
      el('label', { class: 'inline-check', children: [allAppsCb, el('span', { text: ' All applications' })] }),
      appList,
      el('div', { class: 'form-section', text: 'Branches' }),
      field('Allowed branch patterns', branchesI, 'Comma-separated; * is a wildcard (e.g. claude/*). Leave empty to allow any branch. Applies to starting deploys.'),
      el('div', { class: 'form-section', text: 'Expiry' }),
      field('Expires after', expiryS),
      el('div', { class: 'form-actions', children: [createBtn, cancelBtn] }),
    ] });

    body.replaceChildren(form);
    cancelBtn.addEventListener('click', () => { void refresh(); });

    createBtn.addEventListener('click', async () => {
      errBox.setAttribute('hidden', '');
      const scopes = scopeChecks.filter((c) => c.cb.checked).map((c) => c.scope);
      const appIds = allAppsCb.checked
        ? null
        : appChecks.filter((c) => c.cb.checked).map((c) => c.id);
      const branchPatterns = branchesI.value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (scopes.length === 0) {
        errBox.textContent = 'Select at least one permission.';
        errBox.removeAttribute('hidden');
        return;
      }
      if (appIds !== null && appIds.length === 0) {
        errBox.textContent = 'Select at least one application (or check "All applications").';
        errBox.removeAttribute('hidden');
        return;
      }
      const payload: ApiKeyCreateInput = {
        name: nameI.value.trim(),
        scopes,
        appIds,
        branchPatterns: branchPatterns.length > 0 ? branchPatterns : null,
        expiresAt: expiryS.value === '' ? null : Date.now() + Number(expiryS.value) * 86_400_000,
      };
      createBtn.setAttribute('disabled', '');
      try {
        const result = await deps.api.createApiKey(payload);
        showToken(result.token, result.key);
      } catch (err) {
        createBtn.removeAttribute('disabled');
        errBox.textContent = err instanceof ApiError ? err.message : 'Create failed';
        errBox.removeAttribute('hidden');
      }
    });
  };

  // ── One-time token reveal ──────────────────────────────────────
  const showToken = (token: string, key: ApiKeySummary) => {
    const tokenBox = el('pre', { class: 'log-output', text: token });
    const copyBtn = el('button', {
      class: 'primary',
      text: 'Copy token',
      on: {
        click: async () => {
          try {
            await navigator.clipboard.writeText(token);
            copyBtn.textContent = 'Copied ✓';
            setTimeout(() => { copyBtn.textContent = 'Copy token'; }, 2000);
          } catch {
            /* clipboard unavailable; the token is selectable above */
          }
        },
      },
    });
    const doneBtn = el('button', { class: 'ghost', text: 'Done', on: { click: () => { void refresh(); } } });
    const example = [
      '# Start a deploy',
      `curl -X POST '${location.origin}/api/deploy?app=blueeden-app' \\`,
      `  -H 'Authorization: Bearer ${token}' \\`,
      "  -H 'Content-Type: application/json' -d '{\"branch\":\"my-branch\"}'",
      '',
      '# Poll the in-flight deploy / read a log',
      `curl '${location.origin}/api/deploy/current?app=blueeden-app' -H 'Authorization: Bearer ${token}'`,
      `curl '${location.origin}/api/logs?app=blueeden-app' -H 'Authorization: Bearer ${token}'`,
    ].join('\n');
    body.replaceChildren(el('div', { class: 'app-form', children: [
      el('div', { class: 'alert success', text: `Key "${key.name}" created. Copy the token now — it is shown only once.` }),
      tokenBox,
      el('div', { class: 'form-actions', children: [copyBtn, doneBtn] }),
      el('div', { class: 'form-section', text: 'Usage' }),
      el('pre', { class: 'log-output', text: example }),
    ] }));
  };

  addBtn.addEventListener('click', () => openForm());
  void refresh();

  return dismiss;
}
